import { LocationsResponseDto } from '../../../features/locations/Location.model';
import { useRef, useEffect } from 'react';
import * as Styled from './LocationMap.styles';
import WidgetContainer from '../../Atoms/WidgetContainer/WidgetContainer';
import { Wrapper } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { REACT_APP_GOOGLE_MAPS_KEY } from './../../../config';
import { useTheme } from '@mui/material';
import { useLocations } from '../../../features/locations/Location.context';
import { colors } from '../../../themes/muiTheme';

const MapInner = ({
  data,
  defaultZoom = 10,
  mapId = 'b69fd1b5cf9933dc5cc0c5af',
}: {
  data: LocationsResponseDto['content'];
  defaultZoom?: number;
  mapId?: string;
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!containerRef.current) return;

      mapRef.current ??= new google.maps.Map(containerRef.current, {
        center: { lat: 20, lng: 0 },
        zoom: defaultZoom,
        mapId,
        disableDefaultUI: false,
        zoomControl: true,
        fullscreenControl: true,
        minZoom: 2,
        maxZoom: 18,
        gestureHandling: 'cooperative',
      });

      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      if (cancelled) return;

      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }

      markersRef.current.forEach(m => (m.map = null));
      markersRef.current = [];

      const markers = data.map(item => {
        const position = {
          lat: item.coordinates?.latitude ?? 0,
          lng: item.coordinates?.longitude ?? 0,
        };
        const content = document.createElement('div');
        content.style.width = '15px';
        content.style.height = '15px';
        content.style.borderRadius = '50%';
        content.style.border = '1.5px solid black';
        content.style.color = '1.5px solid black';
        content.style.backgroundColor = colors.evp_primary;

        const marker = new AdvancedMarkerElement({
          map: mapRef.current!,
          position,
          title: item.name,
          content,
        });

        return marker;
      });

      markersRef.current = markers;
      clustererRef.current = new MarkerClusterer({ map: mapRef.current, markers });

      const map = mapRef.current;
      if (markers.length) {
        const bounds = new google.maps.LatLngBounds();
        markers.forEach(m => {
          const pos = m.position as google.maps.LatLng | google.maps.LatLngLiteral | null;
          if (pos) bounds.extend(pos);
        });
        map.fitBounds(bounds);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach(m => (m.map = null));
      markersRef.current = [];
    };
  }, [data, defaultZoom, mapId, theme]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: 320 }} />;
};

const LocationMapWidget = () => {
  const { locations, isError } = useLocations();

  return (
    <WidgetContainer
      title='All Locations'
      infoMessage='This map showcases the charging stations of your organization.'
      error={isError}
      aspectRatio='1 / 1.2'
      maxHeight={650}
    >
      <Wrapper apiKey={REACT_APP_GOOGLE_MAPS_KEY}>
        <Styled.Map style={{ width: '100%', height: '100%', minHeight: 320 }}>
          <MapInner data={locations ?? []} />
        </Styled.Map>
      </Wrapper>
    </WidgetContainer>
  );
};

export { LocationMapWidget };
